<template>
  <div class="plan-row" :class="{ 'plan-row-error': errors.length > 0 }">
    <div class="plan-row-fields">
      <b-form-group :label="$t('ia.contents.type')" class="plan-field-type mb-0">
        <v-select
          :value="row.asset_type"
          :options="type_options"
          label="text"
          :reduce="(option) => option.value"
          :selectable="(option) => !option.disabled"
          :clearable="false"
          :searchable="false"
          @input="changeType"
        >
          <template #option="option">
            <span class="d-flex align-items-center justify-content-between">
              {{ option.text }}
              <b-badge v-if="option.disabled" variant="light-secondary" class="ml-50">{{ $t('ia.general.comingSoon') }}</b-badge>
            </span>
          </template>
        </v-select>
      </b-form-group>

      <b-form-group :label="$t('ia.contents.quantity')" class="plan-field-quantity mb-0">
        <b-form-input
          type="number"
          :value="row.quantity"
          min="1"
          :max="max_quantity"
          @input="update({ quantity: $event === '' ? '' : parseInt($event) })"
        ></b-form-input>
      </b-form-group>

      <b-form-group :label="$t('ia.contents.model')" class="plan-field-model mb-0">
        <v-select
          :value="row.model_key"
          :options="model_options"
          label="text"
          :reduce="(option) => option.value"
          :selectable="(option) => !option.disabled"
          :clearable="false"
          :searchable="false"
          :placeholder="$t('ia.contents.selectModel')"
          @input="update({ model_key: $event, option: null })"
        >
          <template #option="option">
            <span class="d-flex align-items-center justify-content-between">
              {{ option.text }}
              <b-badge v-if="option.disabled" variant="light-secondary" class="ml-50">{{ $t('ia.general.comingSoon') }}</b-badge>
            </span>
          </template>
        </v-select>
      </b-form-group>

      <b-form-group :label="row.asset_type === 'video' ? $t('ia.contents.duration') : $t('ia.contents.quality')" class="plan-field-option mb-0">
        <v-select
          :value="row.option"
          :options="option_options"
          label="text"
          :reduce="(option) => option.value"
          :clearable="false"
          :searchable="false"
          :disabled="option_options.length <= 1"
          :placeholder="$t('ia.contents.defaultOption')"
          @input="update({ option: $event })"
        ></v-select>
      </b-form-group>

      <b-button
        variant="flat-secondary"
        class="btn-icon plan-row-remove"
        :disabled="!removable"
        :title="$t('ia.contents.removeRow')"
        @click="$emit('remove')"
      >
        <feather-icon icon="Trash2Icon"></feather-icon>
      </b-button>
    </div>
    <div v-for="(error, index) in errors" :key="index" class="text-danger small mt-50">{{ error }}</div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BButton,
  BBadge,
} from "bootstrap-vue";
import vSelect from 'vue-select';
import {
  modelsForType,
  modelKey,
  rowModel,
  rowOptions,
  sortOptions,
  isModelAvailable,
  isTypeAvailable,
  defaultModelKey,
} from './contents-utils';

export default {
  name: 'PlanRow',
  components: {
    BFormGroup,
    BFormInput,
    BButton,
    BBadge,
    vSelect,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
    models: {
      type: Array,
      default: () => [],
    },
    max_quantity: {
      type: Number,
      default: 20,
    },
    removable: {
      type: Boolean,
      default: true,
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    type_options() {
      return ['image', 'video'].map((asset_type) => ({
        value: asset_type,
        text: this.$t(`ia.contents.${asset_type}`),
        disabled: !isTypeAvailable(this.models, asset_type),
      }));
    },
    model_options() {
      // Available models first, coming soon ones at the end
      return modelsForType(this.models, this.row.asset_type)
        .map((model) => ({ value: modelKey(model), text: model.label, disabled: !isModelAvailable(model) }))
        .sort((a, b) => a.disabled - b.disabled);
    },
    selected_model() {
      return rowModel(this.models, this.row);
    },
    option_options() {
      return [
        { value: null, text: this.$t('ia.contents.defaultOption') },
        ...sortOptions(rowOptions(this.models, this.row)).map((option) => ({ value: option, text: this.optionLabel(option) })),
      ];
    },
  },
  methods: {
    update(changes) {
      this.$emit('update', { ...this.row, ...changes });
    },
    changeType(asset_type) {
      this.update({ asset_type, model_key: defaultModelKey(this.models, asset_type), option: null });
    },
    optionLabel(option) {
      const quality_key = `ia.contents.qualities.${option}`;
      let label = this.$te(quality_key) ? this.$t(quality_key) : option;
      if (this.row.asset_type === 'video') label = `${option} s`;
      const credits = this.selected_model && this.selected_model.credits ? this.selected_model.credits[option] : undefined;
      return credits !== undefined ? `${label} · ${credits} ${this.$t('ia.contents.creditsShort')}` : label;
    },
  },
};
</script>

<style lang="scss" scoped>
.plan-row {
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;

  &.plan-row-error {
    border-color: #ea5455;
  }
}
.plan-row-fields {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;

  .plan-field-type {
    width: 130px;

    ::v-deep .vs__dropdown-menu {
      min-width: 220px;
    }
  }
  .plan-field-quantity {
    width: 90px;
  }
  .plan-field-model {
    flex: 1;
    min-width: 180px;
  }
  .plan-field-option {
    width: 190px;
  }
  .plan-row-remove {
    height: 38px;
  }
}
@media (max-width: 600px) {
  .plan-row-fields {
    .plan-field-type,
    .plan-field-quantity,
    .plan-field-option {
      width: calc(50% - 0.375rem);
    }
    .plan-field-model {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
